import { useCallback, useContext } from "react";
import { StreamingContext } from "./StreamingContextProvider";
import { StreamStatus } from "./types";
import {
  setStreamStatus,
  setStreamContent,
  appendStreamContent,
  clearStreamData,
  setStreamData as setStreamDataAction,
} from "./streamingSlice";

export const useStreaming = () => {
  const context = useContext(StreamingContext);
  if (context === undefined) {
    throw new Error("useStreaming must be used within a StreamingContextProvider");
  }

  const { state, dispatch } = context;

  const updateStreamStatus = useCallback(
    (id: string, status: StreamStatus | undefined) => {
      dispatch(setStreamStatus({ id, status }));
    },
    [dispatch]
  );

  const updateStreamContent = useCallback(
    (id: string, content: string) => {
      dispatch(setStreamContent({ id, content }));
    },
    [dispatch]
  );

  const appendContent = useCallback(
    (id: string, content: string) => {
      dispatch(appendStreamContent({ id, content }));
    },
    [dispatch]
  );

  const clearStream = useCallback(
    (id: string) => {
      dispatch(clearStreamData(id));
    },
    [dispatch]
  );

  const setStreamData = useCallback(
    (id: string, data: { status?: StreamStatus; content?: string }) => {
      dispatch(setStreamDataAction({ id, ...data }));
    },
    [dispatch]
  );

  const getStreamStatus = (id: string) => state.streamStatus[id];
  const getStreamContent = (id: string) => state.streamContent[id] || "";
  const getIsLoading = (id: string) => state.isLoading[id] ?? false;

  return {
    streamStatus: state.streamStatus,
    streamContent: state.streamContent,
    isLoading: state.isLoading,
    updateStreamStatus,
    updateStreamContent,
    appendContent,
    clearStream,
    setStreamData,
    getStreamStatus,
    getStreamContent,
    getIsLoading,
  };
};
